/* Iceberg */
import { Flex } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import {
    BuildCircle,
    DeleteOutline,
    MailOutline,
    SupervisedUserCircle
} from '@mui/icons-material';
import { Button, Grid, IconButton, TableCell, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import { DateFormat } from 'application/Shared/Components/Core/DateFormat';
import CustomTag from 'application/Shared/Components/CustomTag';
import { ManagedTable } from 'application/Shared/Components/Table';
import { DarkTooltip } from 'application/Shared/Components/Tooltips';
import { Helpers, Notify, Response } from 'application/Shared/Helpers';
import { Confirm } from 'application/Shared/Modals/Confirm';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  Objects  */
import { Tenant, TenantName } from 'objects/Tenant';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'router/withRouter';

/*  Components  */
import Create from './Create';

/*  Constants  */
const { isset } = Helpers;

const RenderClientDefault = ({ row }) => {
    const theme = useTheme();
    const { clientDefault } = row;

    if (!clientDefault) {
        return null;
    }

    return (
        <CustomTag
            text="Common.client_default"
            color={theme.palette.icon.clientDefault}
            icon={<BuildCircle />}
        />
    );
};

const RenderClient = ({ row }) => {
    const { tenant, clientDefault } = row;

    if (clientDefault) {
        return <RenderClientDefault row={row} />;
    }

    return (
        <Tenant data={tenant}>
            <div style={{ width: '100%' }}>
                <TenantName />
            </div>
        </Tenant>
    );
};

const RenderTags = ({ row }) => {
    const theme = useTheme();
    const { managedByPartner } = row;

    if (!managedByPartner) {
        return null;
    }

    return (
        <CustomTag
            text="Common.managed_by_partner"
            color={theme.palette.icon.managedByPartner}
            icon={<SupervisedUserCircle />}
            tooltip="alerts.managed_by_partner"
        />
    );
};

const GlobalTagNames = ({ globalTags, tagIds, text }) => {
    const globalTagNames = globalTags.filter((tag) => tagIds.includes(tag.id));

    return (
        <Typography>
            {text}: {globalTagNames.map((tag) => tag.name).join(', ')}
        </Typography>
    );
};

const searchFields = [
    'eventName',
    'destinationsString',
    'conditionsString',
    'dateLastTriggered',
    'tenant.name',
    'tagNamesString'
];

class Configurations extends Component {
    constructor(props) {
        super(props);
        this.state = {
            deleting: false,
            globalTags: [],
            parameters: null,
            rerender: false,
            view: 'configurations'
        };
    }

    componentDidMount() {
        this.load();
    }

    load = async () => {
        try {
            const response = await axios.get('/globalTag', {
                params: {
                    size: -1
                }
            });
            if (!Response.hasData(response)) {
                debug.e('Unable to fetch Global Tags:', response);
                return;
            }
            this.setState({ globalTags: Response.getData(response) });
        } catch (e) {
            debug.e('Error fetching Global Tags:', e);
        }

        const { partner } = this.props;
        if (!partner) {
            this.setState({
                headers: this.renderHeaders(),
                parameters: { includePartnerDefaults: true, size: -1 }
            });
            return;
        }

        this.setState({
            headers: this.renderHeaders(),
            parameters: {
                includeClients: true,
                includeTenant: true,
                managedByPartner: true,
                size: -1
            }
        });
    };

    delete = async (id) => {
        const { t, match } = this.props;
        const { clientId } = match.params;
        this.setState({ deleting: false });

        /* params */
        const params = {};
        if (clientId) {
            params.platformTenantId = clientId;
        }

        try {
            const response = await axios.delete(`/workflowConfig/${id}`, {
                params
            });
            if (!Response.hasResponse(response)) {
                debug.e('Unable to delete alert:', response);
                Notify.e(t('Common.error'));
                return;
            }

            Notify.success(t('Alerts.delete_success'));
            this.rerender();
        } catch (e) {
            debug.e('Error adding alert:', e);
            Notify.e(t('Common.error'));
        }
    };

    rerender = () => {
        this.setState({ rerender: true }, () =>
            this.setState({ rerender: false })
        );
    };

    handlePreprocessRows = (rows) => {
        const { config } = this.props;

        return rows.map((row) => {
            const { event, destinations, conditions } = row;
            let eventName = event;

            // Convert event key to the event name
            if (isset(config, event, 'name')) {
                eventName = config[event].name;
            }

            // Join destination emails into searchable string
            const destinationsString = Helpers.join(destinations, ', ');

            // Join tag names into searchable string
            let tagNamesString = '';
            if (conditions.tag) {
                const tagIds = conditions.tag.value;

                const tagNames = this.state.globalTags.filter((tag) =>
                    tagIds.includes(tag.id)
                );
                tagNamesString = tagNames.map((tag) => tag.name).join(', ');
            }

            // Extract condition values and join them into a searchable string
            let conditionsString = '';
            if (conditions) {
                const conditionKeys = Object.keys(conditions);
                conditionsString = conditionKeys
                    .map((key) => {
                        const condition = conditions[key];
                        if (condition && condition.value) {
                            if (Array.isArray(condition.value)) {
                                return condition.value.join(' ');
                            }
                            return String(condition.value);
                        }
                        return '';
                    })
                    .filter(Boolean)
                    .join(' ')
                    .replace(/_/g, ' '); // Replace underscores with spaces
            }

            return {
                ...row,
                conditionsString,
                destinationsString,
                eventName,
                tagNamesString
            };
        });
    };

    handleChangeView = (view) => {
        this.setState({ view });
    };

    renderHeaders = () => {
        const { t, partner } = this.props;

        const headers = [
            {
                field: 'workflowEvent',
                renderHeader: t('Alert.event'),
                width: '20%'
            },

            {
                field: 'conditions',
                renderHeader: t('Common.conditions'),
                width: '15%'
            },

            {
                field: 'workflowDeliveryMethod',
                renderHeader: t('Alert.delivery_method'),
                width: '10%'
            },
            {
                field: 'destinations',
                renderHeader: t('Common.destinations'),
                width: '15%'
            },
            {
                field: 'workflowLastTriggered',
                renderHeader: t('Alert.last_triggered'),
                width: '15%'
            }
        ];

        if (partner) {
            headers.push({
                field: 'client',
                renderHeader: t('Common.client'),
                width: '20%'
            });
        } else {
            headers.push({
                field: 'tags',
                renderHeader: t('Common.tags'),
                width: '20%'
            });
        }

        headers.push({
            align: 'right',
            field: 'actions',
            renderHeader: t('Common.actions'),
            width: '5%'
        });

        return headers;
    };

    renderConditions = (params) => {
        const { globalTags } = this.state;
        const { operands, t } = this.props;
        const { conditions, eventConfig } = params;
        if (Helpers.isEmpty(conditions)) {
            return <span>N/A</span>;
        }

        return (
            <>
                {Object.keys(conditions).map((key) => {
                    if (!isset(eventConfig, 'conditions', key)) {
                        return null;
                    }
                    const conditionCfg = eventConfig.conditions[key];
                    const condition = conditions[key];

                    const { hidden, name, shortName, picker } = conditionCfg;
                    const { number } = condition;
                    let { value } = condition;

                    if (hidden) {
                        return null;
                    }

                    let text = name;
                    if (shortName) {
                        text = shortName;
                    }

                    if (number) {
                        const { operand } = number;
                        text += ` ${operands[operand]} ${value}`;
                    }

                    if (picker) {
                        if (value === '0') {
                            value = 'All';
                        }

                        if (
                            Array.isArray(value) &&
                            picker.type === 'Lifecycle'
                        ) {
                            // We need to translate the lifecycle stages to make them look nice.
                            text += `: ${value
                                .map((val) => t(`Picker.lifecycle.${val}`))
                                .join(', ')}`;
                        } else if (
                            Array.isArray(value) &&
                            picker.type === 'Tag'
                        ) {
                            text = (
                                <GlobalTagNames
                                    globalTags={globalTags}
                                    tagIds={value}
                                    text={text}
                                />
                            );
                        } else {
                            text += `: ${Helpers.join(value, ', ')}`;
                        }
                    }

                    return (
                        <Grid item xs={12} key={key}>
                            {text}
                        </Grid>
                    );
                })}
            </>
        );
    };

    renderActions = (row) => {
        const { t, partner } = this.props;
        const { id, managedByPartner } = row;

        if (managedByPartner && !partner) {
            return null;
        }

        return (
            <DarkTooltip arrow title={t('Tooltips.alert.delete')}>
                <IconButton
                    size="small"
                    color="secondary"
                    onClick={() => this.setState({ deleting: id })}
                >
                    <DeleteOutline />
                </IconButton>
            </DarkTooltip>
        );
    };

    renderRow = (row) => {
        const { config, methods, partner } = this.props;
        const { conditions, method, destinations, dateLastTriggered } = row;

        let { event } = row;
        let eventConfig = {};
        if (isset(config, event)) {
            eventConfig = config[event];
            event = eventConfig.name;
        }

        return (
            <>
                <TableCell>{event}</TableCell>
                <TableCell>
                    {this.renderConditions({ conditions, eventConfig })}
                </TableCell>
                <TableCell>
                    <MailOutline /> {methods[method]}
                </TableCell>
                <TableCell>{Helpers.join(destinations, ', ')}</TableCell>
                <TableCell>
                    <DateFormat date={dateLastTriggered} />
                </TableCell>
                <TableCell>
                    {partner ? (
                        <RenderClient row={row} />
                    ) : (
                        <RenderTags row={row} />
                    )}
                </TableCell>
                <TableCell align="right">{this.renderActions(row)}</TableCell>
            </>
        );
    };

    renderCreateAlert = () => {
        const { events, defaults, config, methods, operands, partner } =
            this.props;
        const { view } = this.state;

        if (view !== 'create') {
            return null;
        }

        return (
            <Create
                events={events}
                defaults={defaults}
                config={config}
                methods={methods}
                operands={operands}
                handleChangeView={this.handleChangeView}
                partner={partner}
            />
        );
    };

    renderConfigurations = () => {
        const { t } = this.props;
        const { deleting, headers, parameters, rerender, view } = this.state;

        if (view !== 'configurations') {
            return null;
        }

        return (
            <Grid item xs={12}>
                <Flex sx={{ justifyContent: 'flex-end', pt: '8px' }}>
                    <Button
                        color="primary"
                        data-trackid="createWorkflow"
                        variant="contained"
                        onClick={() => this.handleChangeView('create')}
                    >
                        {t('Alert.tab_create')}
                    </Button>
                </Flex>
                {deleting && (
                    <Confirm
                        data-trackid="deleteWorkflow"
                        modalOpen={!!deleting}
                        promptConfirmFn={() => this.delete(deleting)}
                        promptCancelFn={() => {
                            this.setState({ deleting: false });
                        }}
                        promptTitle={t('Alert.confirm_delete')}
                        promptText={t('Common.default_confirm_text')}
                    />
                )}
                <ManagedTable
                    headers={headers}
                    parameters={parameters}
                    renderBody={this.renderRow}
                    renderBodyKey="id"
                    renderFooter
                    rerender={rerender}
                    rowsApi="/workflowConfig"
                    rowsApiPreload
                    rowsPerPage={10}
                    searchEnabled
                    searchFields={searchFields}
                    sortDirection="DESC"
                    sortFieldType="string"
                    sortKey="event"
                    preprocessRows={this.handlePreprocessRows}
                />
            </Grid>
        );
    };

    render() {
        return (
            <>
                {this.renderCreateAlert()}
                {this.renderConfigurations()}
            </>
        );
    }
}
export default withRouter(withTranslation()(Configurations));
